<template>
    <div>
        <section class="left w-full max-w-7xl bg-white border border-gray-300 rounded-02">
            <div class="header flex items-center justify-between py-[22px] px-[26px] border-gray-300 border-b">
                <span class="font-inter-heading-18 text-content-basic-1 whitespace-normal break-words"> 
                    {{ companyName }}
                </span>
                <div class="flex items-center gap-3">
                    <ElementsTooltipSingleton :ref="(el)=>{ if(el) __tooltip = el }" />
                    <div class="select-none"
                         :ref="(el)=>{ if (el && __tooltip) { __tooltip.loadBasic(el, ()=> 'Departments already assigned to another sub-admin cannot be selected.', { placement: 'top' }); } }">
                        <InfoIcon :width="24" :height="24" color="text-gray-600" weight="REGULAR" />
                    </div>
                </div>

            </div>
            <div class="ml-4 overflow-auto max-h-[640px]">
                <div v-for="(department, index) in showDepartments" v-bind:key="index">
					<div
						@click="clickDepartment(department)"
						class="mr-4 relative h-[60px] flex items-center rounded group"
						:class="[
							isDisabled(department.departmentSeq) ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer'
						]"
						:style="{ 'padding-left': `${20 * department.level}px` }">
						<!-- hover/selected background from icon position to right end -->
						<div
							class="absolute inset-y-0 right-0 rounded-02 z-0 pointer-events-none transition"
							:class="[
								(isSelected(department.departmentSeq) && !isDisabled(department.departmentSeq)) ? 'bg-blue-50 border border-primary-3' : '',
								!isDisabled(department.departmentSeq) ? 'group-hover:bg-blue-50' : ''
							]"
							:style="{ left: `${(20 * department.level)}px` , top: '4px', bottom: '4px' }"
						/>
						<!-- clickable content: icon + texts -->
						<div class="relative z-10 flex items-center w-full">
							<div class="w-[24px] h-[24px] flex items-center justify-center">
								<OrgRootIcon v-if="department.level === 0" 
									:width="20" 
									:height="20" 
									:color="isDisabled(department.departmentSeq) ? 'text-gray-400' : 'text-content-basic-2'" 
								/>
								<OrgLayerIcon v-else 
									:level="department.level" 
									:width="20" 
									:color="isDisabled(department.departmentSeq) ? 'text-gray-400' : 'text-content-basic-2'" 
								/>
							</div>
							<div class="max-w-[80%] flex-1 flex items-center gap-1 ml-3">
								<div class="font-inter-body-1 truncate"
                                    :class="{
                                        'text-gray-400': isDisabled(department.departmentSeq),
                                        'text-content-basic-1': !isDisabled(department.departmentSeq) && !isSelected(department.departmentSeq),
                                        'text-primary-3': !isDisabled(department.departmentSeq) && isSelected(department.departmentSeq),
                                     }">
									{{ department.departmentName }}
								</div>
								<div class="font-inter-body-1"
									 :class="{
                                        'text-gray-400': isDisabled(department.departmentSeq),
                                        'text-gray-600': !isDisabled(department.departmentSeq) && !isSelected(department.departmentSeq),
                                        'text-primary-3': !isDisabled(department.departmentSeq) && isSelected(department.departmentSeq),
                                     }">
									{{ `(${department.employeeCount})` }}
								</div>
							</div>
							<!-- selected check icon -->
                            <div v-if="isSelected(department.departmentSeq) && !isDisabled(department.departmentSeq)" class="absolute right-4 z-10">
                                <CheckIcon :width="24" :height="24" color="text-primary-3" weight="FILL" />
                            </div>
						</div>
                        <img
                            v-for="(c, index) in (department.css || [])"
                            :key="`line-top-${index}`"
                            :src="require(`@/assets/img/department-${c.top}.png`)"
                            class="absolute top-0 w-[20px] h-[20px] z-0 pointer-events-none"
                            :style="{ left: `${20 * index}px` }"
                        />
                        <img
                            v-for="(c, index) in (department.cssMid || [])"
                            :key="`line-mid-${index}`"
                            :src="require(`@/assets/img/department-${c.middle}.png`)"
                            class="absolute top-[20px] w-[20px] h-[20px] z-0 pointer-events-none"
                            :style="{ left: `${20 * index}px` }"
                        />
                        <img
                            v-for="(c, index) in (department.css || [])"
                            :key="`line-bot-${index}`"
                            :src="require(`@/assets/img/department-${c.bottom}.png`)"
                            class="absolute bottom-0 h-[20px] z-0 pointer-events-none"
                            :style="{ left: `${20 * index}px` }"
                        />
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import InfoIcon from '@/components/design-system/icon/info.vue';
import ElementsTooltipSingleton from '@/components/elements/ElementsTooltipSingleton.vue';
import OrgRootIcon from '@/components/design-system/icon/org-root.vue';
import OrgLayerIcon from '@/components/design-system/icon/org-layer.vue';
import CheckIcon from '@/components/design-system/icon/check.vue';
export default {
    components: { InfoIcon, ElementsTooltipSingleton, OrgRootIcon, OrgLayerIcon, CheckIcon },
    props : {
        // currently selected department sequences for highlighting
        selectedSeqs: {
            type: Array,
            default: () => [],
        },
        // disabled departments (show but cannot select)
        disabledSeqs: {
            type: Array,
            default: () => [],
        },  
    },
    data() {
        return {
            departments: [],
            showDepartments: [],
            returnSubArray: [],
            companyName: '',
            __tooltip: null,
        };
    },
    mounted() {
        const self = this;
        self.getDepartmentData();
        self.getCompanyName();
    },
    emits: ['clickDepartment', 'clickToggle'],
    methods: {
        getCompanyName() {
            const self = this;
            const url = self.$api('uri', 'get-company');
            self.$axios.get(url).then((res) => {
                self.companyName = res.data.data.companyName;
            });
        },
        clickDepartment(item) {
            const self = this;
            // 비활성 부서는 선택 불가
            if (this.isDisabled(item.departmentSeq)) return;
            // 부모에게 클릭된 부서 전달 (토글은 부모가 처리)
            self.$emit('clickDepartment', item.departmentSeq);
        },
        isSelected(departmentSeq) {
            return Array.isArray(this.selectedSeqs) && this.selectedSeqs.includes(departmentSeq);
        },
        isDisabled(departmentSeq) {
            return Array.isArray(this.disabledSeqs) && this.disabledSeqs.includes(departmentSeq);
        },
        getDepartmentData() {
            const self = this;
            let getId = [];
            self.showDepartments = [];
            self.returnSubArray = [];

            let url = self.$api('uri', 'get-department');

            const json_query = JSON.stringify({
                showRootNode: true,
                excludeEmployeeStatusList: ['RESIGNED'],
            });

            self.$axios
                .get(url, { params: { json_query } })
                .then(function (res) {
                    self.departments = res.data.data.list;

                    self.pushToShowDepartments(self.findTopLevel());
                    getId.push(self.findTopLevel().departmentSeq);
                    self.pushToShowDepartments(self.findUpperIsTopId(getId, 1));

                    return self.showDepartments;
                })
                .then((departmentTree) => {
                    //CSS 출력
                    for (let i = 0; i < departmentTree.length; i++) {
                        self.drawMyLevelCss(i);
                    }
                })
                .catch((error) => {
                    console.error('error => ', error);
                });
        },
        pushToShowDepartments(department) {
            const self = this;

            if (department.length == null) {
                self.showDepartments.push({ ...department, showSub: false });
            }

            for (let i = 0; i < department.length; i++) {
                self.showDepartments.push({ ...department[i], showSub: false });
            }
        },
        findTopLevel() {
            const self = this;
            const length = self.departments.length;

            for (let i = 0; i < length; i++) {
                if (self.departments[i].parentDepartmentSeq == null) {
                    self.departments[i].level = 0;
                    return self.departments[i];
                }
            }
        },
        drawMyLevelCss(index) {
            //전역
            const self = this;
            const department = self.showDepartments[index];
            const myLevel = department.level;
            let cssArr = [];

            //init
            self.returnSubArray = [];

            //0. root node
            if (self.findChildList(department.departmentSeq).length == 0) {
                cssArr.unshift({ top: 15, middle: myLevel + 1, bottom: 15 });
            } else {
                cssArr.unshift({ top: 15, middle: myLevel + 1, bottom: 12 });
            }

            //1. level 1
            if (myLevel >= 1) {
                if (self.findMyPosition(department, myLevel)) cssArr.unshift({ top: 12, middle: 11, bottom: 15 });
                else cssArr.unshift({ top: 12, middle: 13, bottom: 12 });
            }

            //2. level n (1 이상)
            if (myLevel > 1) self.drawMyNextLevelCss(department, cssArr);

            //적용: 전체 라인용 배열과, 가운데(아이콘 영역)에서 현재 노드 칸을 제외한 전용 배열을 함께 저장
            department.css = cssArr;
            department.cssMid = Array.isArray(cssArr) && cssArr.length > 0 ? cssArr.slice(0, -1) : [];
        },
        findUpperIsTopId(id, level) {
            const self = this;

            for (let i = 0; i < self.departments.length; i++) {
                if (self.departments[i].parentDepartmentSeq == id) {
                    self.departments[i].level = level;
                    self.returnSubArray.push(self.departments[i]);
                    self.findUpperIsTopId(self.departments[i].departmentSeq, level + 1);
                }
            }
            return self.returnSubArray;
        },
        findChildList(id) {
            const self = this;
            const returnArr = [];

            self.findUpperIsTopId(id).forEach((d) => {
                returnArr.push(d.departmentSeq);
            });

            return returnArr;
        },
        findMyPosition(department) {
            const self = this;
            const siblingArr = [];
            const parentSeq = department.parentDepartmentSeq;
            let isLast = false;

            self.showDepartments.forEach((d) => {
                if (d.parentDepartmentSeq == parentSeq) siblingArr.push(d.departmentSeq);
            });

            const myLevelIndex = siblingArr.indexOf(department.departmentSeq);
            if (siblingArr.length == myLevelIndex + 1) isLast = true;
            else isLast = false;

            return isLast;
        },
        drawMyNextLevelCss(department, cssArr) {
            const self = this;
            const parentSeq = department.parentDepartmentSeq;
            const parent = self.showDepartments.find((d) => d.departmentSeq == parentSeq);
            const parentLevel = parent.level;

            if (self.findMyPosition(parent, parentLevel)) cssArr.unshift({ top: 15, middle: 15, bottom: 15 });
            else cssArr.unshift({ top: 12, middle: 12, bottom: 12 });

            //언제까지 ? deparmt의 레벨이 2보다 작아지면 ?
            if (parentLevel > 1) self.drawMyNextLevelCss(parent, cssArr);

            return cssArr;
        },
    },
};
</script>

